import {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { ParameterItem, SQLParameter } from './SQLParameter';
import { StyledAddParameterButton, StyledParameterList } from './styles';
import { OutlineInputControl } from '__components/Inputs/InputControl';

export const SQLParameterList: FC<{
  sqlParameters: ParameterItem[];
  setSqlParameters: Dispatch<SetStateAction<ParameterItem[]>>;
}> = ({ sqlParameters, setSqlParameters }) => {
  const { t } = useTranslation();

  const sectionRef = useRef<HTMLDivElement>(null);

  const onChangeSQLParameters = useCallback(
    (parameter: ParameterItem, index: number) => {
      setSqlParameters((prev) =>
        prev.map((item, i) => (i === index ? parameter : item))
      );
    },
    [setSqlParameters]
  );

  const onAddSQLParameter = useCallback(() => {
    setSqlParameters((prev) => [
      ...prev,
      { id: uuidv4(), name: '', value: '' },
    ]);
    setTimeout(() => {
      sectionRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  }, [setSqlParameters]);

  const onDeleteSQLParameter = useCallback(
    (index: number) => {
      setSqlParameters((prev) => prev.filter((_, i) => i !== index));
    },
    [setSqlParameters]
  );

  const uniqueNames = useMemo(
    () => sqlParameters.map((param) => param.name),
    [sqlParameters]
  );

  return (
    <OutlineInputControl label={t('SQL Parameters')}>
      <StyledParameterList>
        {sqlParameters.map((parameter, index) => (
          <SQLParameter
            key={parameter.id}
            parameter={parameter}
            onChange={onChangeSQLParameters}
            onDelete={onDeleteSQLParameter}
            index={index}
            uniqueNames={uniqueNames}
          />
        ))}
      </StyledParameterList>
      <div ref={sectionRef} />
      <StyledAddParameterButton variant="text" onClick={onAddSQLParameter}>
        {t('+ Add Parameter')}
      </StyledAddParameterButton>
    </OutlineInputControl>
  );
};
